import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/opt/build/repo/www/src/layouts/ApiLayout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "migrating-to-v2"
    }}>{`Migrating to v2`}</h1>
    <p>{`React-Bootstrap v2 adds full compatibility with Bootstrap 5. Because Bootstrap 5 is a major rewrite of the project, there
are significant breaking changes from React-Bootstrap v1.`}</p>
    <p><strong parentName="p">{`PLEASE FIRST READ THE UPSTREAM BOOSTRAP MIGRATION GUIDE`}</strong></p>
    <blockquote>
      <p parentName="blockquote"><a parentName="p" {...{
          "href": "https://v5.getbootstrap.com/docs/5.0/migration"
        }}>{`https://v5.getbootstrap.com/docs/5.0/migration`}</a></p>
    </blockquote>
    <p>{`React-Bootstrap `}<em parentName="p">{`only`}</em>{` contains components that are present in vanilla Bootstrap. If functionality
was removed from Bootstrap, then it was also removed from React-Bootstrap. This guide does not
repeat information found in the upstream migration guide. Its goal is to document React-Bootstrap-specific
API changes and additions.`}</p>
    <h2 {...{
      "id": "versioning"
    }}>{`Versioning`}</h2>
    <p>{`We will continue to provide general maintenance for Bootstrap 4 components, because there are many projects that continue to depend on Bootstrap 4 support in
`}<inlineCode parentName="p">{`react-bootstrap`}</inlineCode>{`. `}<inlineCode parentName="p">{`react-bootstrap`}</inlineCode>{` package versions will be as follows:`}</p>
    <ul>
      <li parentName="ul">{`Bootstrap 3 support will continue in react-bootstrap versions < `}<inlineCode parentName="li">{`v1.0.0`}</inlineCode></li>
      <li parentName="ul">{`Bootstrap 4 support will be in react-bootstrap versions >= `}<inlineCode parentName="li">{`v1.0.0`}</inlineCode></li>
      <li parentName="ul">{`Bootstrap 5 support will be in react-bootstrap versions >= `}<inlineCode parentName="li">{`v2.0.0`}</inlineCode></li>
    </ul>
    <p>{`We are `}<strong parentName="p">{`not`}</strong>{` committing to keeping breaking changes in lockstep with bootstraps major releases,
there may be a React-Bootstrap v3 targeting Bootstrap 6 depending on what's best for the project.`}</p>
    <h2 {...{
      "id": "summary-of-breaking-changes-from-v1x"
    }}>{`Summary of breaking changes from v1.x`}</h2>
    <p>{`Below is a `}<em parentName="p">{`rough`}</em>{` account of the breaking API changes as well as the minimal change to migrate`}</p>
    <h3 {...{
      "id": "general"
    }}>{`General`}</h3>
    <ul>
      <li parentName="ul">{`React >= 16.14.0 is now required to support the new JSX transform`}</li>
    </ul>
    <h3 {...{
      "id": "accordion"
    }}>{`Accordion`}</h3>
    <ul>
      <li parentName="ul">{`accordion is no longer card-based and has been rewritten with different markup.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`AccordionContext`}</inlineCode>{`'s value interface has changed.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`AccordionToggle`}</inlineCode>{` has been renamed to `}<inlineCode parentName="li">{`AccordionButton`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`useAccordionToggle`}</inlineCode>{` has been renamed to `}<inlineCode parentName="li">{`useAccordionButton`}</inlineCode>{`.`}</li>
    </ul>
    <h3 {...{
      "id": "buttongroup"
    }}>{`ButtonGroup`}</h3>
    <ul>
      <li parentName="ul">{`removed `}<inlineCode parentName="li">{`toggle`}</inlineCode>{`.`}</li>
    </ul>
    <h3 {...{
      "id": "closebutton"
    }}>{`CloseButton`}</h3>
    <ul>
      <li parentName="ul">{`Removed `}<inlineCode parentName="li">{`label`}</inlineCode>{` prop in favor of `}<inlineCode parentName="li">{`aria-label`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Added `}<inlineCode parentName="li">{`variant`}</inlineCode>{` prop.`}</li>
    </ul>
    <h3 {...{
      "id": "col"
    }}>{`Col`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`ColOrder`}</inlineCode>{` is now maximum 5 instead of 12.`}</li>
    </ul>
    <h3 {...{
      "id": "dropdown"
    }}>{`Dropdown`}</h3>
    <ul>
      <li parentName="ul">{`dropdown dividers use `}<inlineCode parentName="li">{`hr`}</inlineCode>{` by default instead of `}<inlineCode parentName="li">{`div`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Alignment values `}<inlineCode parentName="li">{`left`}</inlineCode>{` and `}<inlineCode parentName="li">{`right`}</inlineCode>{` changed to `}<inlineCode parentName="li">{`start`}</inlineCode>{` and `}<inlineCode parentName="li">{`end`}</inlineCode>{` respectively.`}</li>
      <li parentName="ul">{`Removed `}<inlineCode parentName="li">{`alignRight`}</inlineCode>{`. Use `}<inlineCode parentName="li">{`align="end"`}</inlineCode>{` instead.`}</li>
    </ul>
    <h3 {...{
      "id": "dropdownbutton"
    }}>{`DropdownButton`}</h3>
    <ul>
      <li parentName="ul">{`Removed `}<inlineCode parentName="li">{`menuAlign`}</inlineCode>{` prop in favor of `}<inlineCode parentName="li">{`align`}</inlineCode>{`.`}</li>
    </ul>
    <h3 {...{
      "id": "dropdownmenu"
    }}>{`DropdownMenu`}</h3>
    <ul>
      <li parentName="ul">{`Removed `}<inlineCode parentName="li">{`alignRight`}</inlineCode>{`. Use `}<inlineCode parentName="li">{`align="end"`}</inlineCode>{` instead.`}</li>
    </ul>
    <h3 {...{
      "id": "form"
    }}>{`Form`}</h3>
    <ul>
      <li parentName="ul">{`removed `}<inlineCode parentName="li">{`inline`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`removed `}<inlineCode parentName="li">{`bsPrefix`}</inlineCode>{`.`}</li>
    </ul>
    <h3 {...{
      "id": "formcheck"
    }}>{`FormCheck`}</h3>
    <ul>
      <li parentName="ul">{`removed `}<inlineCode parentName="li">{`bsCustomPrefix`}</inlineCode>{` and `}<inlineCode parentName="li">{`custom`}</inlineCode>{` in favor of `}<inlineCode parentName="li">{`bsSwitchPrefix`}</inlineCode>{`.`}</li>
    </ul>
    <h4 {...{
      "id": "formcheckinput"
    }}>{`FormCheckInput`}</h4>
    <ul>
      <li parentName="ul">{`removed `}<inlineCode parentName="li">{`bsCustomPrefix`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`removed `}<inlineCode parentName="li">{`isStatic`}</inlineCode>{`.`}</li>
    </ul>
    <h4 {...{
      "id": "formchecklabel"
    }}>{`FormCheckLabel`}</h4>
    <ul>
      <li parentName="ul">{`removed `}<inlineCode parentName="li">{`bsCustomPrefix`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`changed base class from `}<inlineCode parentName="li">{`form-check-input`}</inlineCode>{` to `}<inlineCode parentName="li">{`form-check-label`}</inlineCode>{`.`}</li>
    </ul>
    <h3 {...{
      "id": "formcontrol"
    }}>{`FormControl`}</h3>
    <ul>
      <li parentName="ul">{`dropped `}<inlineCode parentName="li">{`as`}</inlineCode>{` prop supporting `}<inlineCode parentName="li">{`select`}</inlineCode>{` tags in favor of FormSelect.`}</li>
      <li parentName="ul">{`removed `}<inlineCode parentName="li">{`custom`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`removed `}<inlineCode parentName="li">{`bsCustomPrefix`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`dropped support for `}<inlineCode parentName="li">{`type`}</inlineCode>{` supporting the `}<inlineCode parentName="li">{`range`}</inlineCode>{` value in favor of FormRange.`}</li>
      <li parentName="ul">{`add color picker`}</li>
    </ul>
    <h3 {...{
      "id": "formfile"
    }}>{`FormFile`}</h3>
    <ul>
      <li parentName="ul">{`dropped all file-related components. Use `}<inlineCode parentName="li">{`<Form.Control type="file" />`}</inlineCode>{` instead.`}</li>
    </ul>
    <h3 {...{
      "id": "formgroup"
    }}>{`FormGroup`}</h3>
    <ul>
      <li parentName="ul">{`removed `}<inlineCode parentName="li">{`bsPrefix`}</inlineCode>{`. The `}<inlineCode parentName="li">{`.form-group`}</inlineCode>{` class is no longer supported in Bootstrap,
but this component is useful for passing `}<inlineCode parentName="li">{`controlId`}</inlineCode>{` to labels and inputs. To handle
spacing, use margin utilities instead.`}</li>
    </ul>
    <h3 {...{
      "id": "formrow"
    }}>{`FormRow`}</h3>
    <ul>
      <li parentName="ul">{`removed. Use `}<inlineCode parentName="li">{`Row`}</inlineCode>{` instead.`}</li>
    </ul>
    <h3 {...{
      "id": "inputgroup"
    }}>{`InputGroup`}</h3>
    <ul>
      <li parentName="ul">{`dropped `}<inlineCode parentName="li">{`InputGroupPrepend`}</inlineCode>{` and `}<inlineCode parentName="li">{`InputGroupAppend`}</inlineCode>{`. Buttons and `}<inlineCode parentName="li">{`InputGroupText`}</inlineCode>{` can now be added as direct children.`}</li>
    </ul>
    <h3 {...{
      "id": "popover"
    }}>{`Popover`}</h3>
    <ul>
      <li parentName="ul">{`renamed `}<inlineCode parentName="li">{`content`}</inlineCode>{` prop to `}<inlineCode parentName="li">{`body`}</inlineCode>{` to coincide with PopoverContent component name change.`}</li>
    </ul>
    <h3 {...{
      "id": "popovercontent"
    }}>{`PopoverContent`}</h3>
    <ul>
      <li parentName="ul">{`renamed to PopoverBody to match class name.`}</li>
    </ul>
    <h3 {...{
      "id": "popovertitle"
    }}>{`PopoverTitle`}</h3>
    <ul>
      <li parentName="ul">{`renamed to PopoverHeader to match class name.`}</li>
    </ul>
    <h3 {...{
      "id": "responsiveembed"
    }}>{`ResponsiveEmbed`}</h3>
    <ul>
      <li parentName="ul">{`renamed to Ratio.`}</li>
      <li parentName="ul">{`aspectRatio options changed to '1x1' | '4x3' | '16x9' | '21x9'.`}</li>
    </ul>
    <h3 {...{
      "id": "splitbutton"
    }}>{`SplitButton`}</h3>
    <ul>
      <li parentName="ul">{`Removed `}<inlineCode parentName="li">{`menuAlign`}</inlineCode>{` prop in favor of `}<inlineCode parentName="li">{`align`}</inlineCode>{`. `}</li>
    </ul>
    <h3 {...{
      "id": "toast"
    }}>{`Toast`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`delay`}</inlineCode>{` is now default 5000 ms.`}</li>
    </ul>
    <h3 {...{
      "id": "togglebutton"
    }}>{`ToggleButton`}</h3>
    <ul>
      <li parentName="ul">{`add `}<inlineCode parentName="li">{`bsPrefix`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`id`}</inlineCode>{` is now required. This is to allow toggling of the input due to markup changes in Bootstrap.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      